"use client"

import { useState, useCallback } from "react"
import { m, AnimatePresence } from "framer-motion"
import { Zap, Search, ShieldCheck, BadgeDollarSign, ChevronLeft, ChevronRight } from "lucide-react"

const FEATURES = [
  {
    icon: Zap,
    title: "Reparo Expresso",
    description: "Solução ágil para você voltar a usar seu device.",
    color: "#00F0FF",
  },
  {
    icon: Search,
    title: "Diagnóstico Gratuito",
    description: "Avaliação completa do seu aparelho sem custo.",
    color: "#00A3FF",
  },
  {
    icon: ShieldCheck,
    title: "Garantia Real Estendida",
    description: "Segurança de verdade nos nossos serviços.",
    color: "#B829FF",
  },
  {
    icon: BadgeDollarSign,
    title: "Preço Justo",
    description: "Qualidade premium sem cobrar o olho da cara.",
    color: "#FF6B35",
  },
]

const slideVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 200 : -200,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction: number) => ({
    x: direction < 0 ? 200 : -200,
    opacity: 0,
  }),
}

export function Trust() {
  const [[page, direction], setPage] = useState([0, 0])

  const slideIndex = ((page % FEATURES.length) + FEATURES.length) % FEATURES.length

  const paginate = useCallback((newDirection: number) => {
    setPage([page + newDirection, newDirection])
  }, [page])

  const feature = FEATURES[slideIndex]

  return (
    <section 
      id="about" 
      className="py-24 bg-gradient-to-b from-[#050508] to-[#0A0A0F] border-y border-[#B829FF]/30 relative overflow-hidden"
      style={{ contain: "layout style paint" }}
    >
      <div className="absolute inset-0 pointer-events-none">
        <div 
          className="absolute top-0 left-1/3 w-[400px] h-[400px] bg-[#B829FF]/5 rounded-full blur-[100px]"
          style={{ willChange: "opacity" }}
        />
        <div 
          className="absolute bottom-0 right-1/3 w-[400px] h-[400px] bg-[#00A3FF]/5 rounded-full blur-[100px]"
          style={{ willChange: "opacity" }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <m.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px", amount: 0.3 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          style={{ willChange: "transform, opacity" }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-black font-[family-name:var(--font-orbitron)] text-white mb-4">
            Por Que Escolher a NITROX STORE?
          </h2>
          <p className="text-slate-100 text-lg font-medium">
            Excelência e confiança em cada atendimento.
          </p>
        </m.div>

        <div className="relative max-w-lg mx-auto">
          {/* Carousel Container */}
          <div className="relative h-[320px] overflow-hidden">
            <AnimatePresence initial={false} custom={direction} mode="wait">
              <m.article
                key={page}
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{
                  x: { type: "spring", stiffness: 250, damping: 25 },
                  opacity: { duration: 0.15 },
                }}
                style={{ willChange: "transform, opacity", borderColor: `${feature.color}30` }}
                className="absolute inset-0 p-8 glass-cyber rounded-2xl text-center border"
              >
                {/* Scanline effect */}
                <div className="absolute inset-0 overflow-hidden rounded-2xl pointer-events-none">
                  <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[#00F0FF]/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>

                <div
                  className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-6"
                  style={{
                    background: `linear-gradient(135deg, ${feature.color}40, ${feature.color}20)`,
                    boxShadow: `0 0 20px ${feature.color}30`,
                  }}
                >
                  <feature.icon className="w-8 h-8" style={{ color: feature.color }} />
                </div>

                <h3 className="text-xl font-bold font-[family-name:var(--font-orbitron)] text-white mb-4">
                  {feature.title}
                </h3>
                <p className="text-slate-200 leading-relaxed">
                  {feature.description}
                </p>
              </m.article>
            </AnimatePresence>
          </div>

          {/* Navigation Arrows */}
          <button
            onClick={() => paginate(-1)}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-12 w-12 h-12 flex items-center justify-center rounded-full bg-[#1a1a2e]/80 border border-white/10 text-white hover:bg-[#B829FF]/20 hover:border-[#B829FF]/50 transition-colors z-10"
            aria-label="Item anterior"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={() => paginate(1)}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-12 w-12 h-12 flex items-center justify-center rounded-full bg-[#1a1a2e]/80 border border-white/10 text-white hover:bg-[#B829FF]/20 hover:border-[#B829FF]/50 transition-colors z-10"
            aria-label="Próximo item"
          >
            <ChevronRight className="w-5 h-5" />
          </button>

          {/* Dots Indicator */}
          <div className="flex justify-center gap-2 mt-6">
            {FEATURES.map((_, index) => (
              <button
                key={index}
                onClick={() => setPage([index, index > slideIndex ? 1 : -1])}
                className={`h-2 rounded-full transition-all duration-300 ${
                  index === slideIndex
                    ? "w-6 bg-[#B829FF]"
                    : "w-2 bg-white/30 hover:bg-white/50"
                }`}
                aria-label={`Ir para item ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}
